import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { IonAccordionGroup } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ISummaryPrice } from '../../../../private/shared/models/productos';

@Component({
  selector: 'getm-purchase-limits-bar',
  templateUrl: './purchase-limits-bar.component.html',
  styleUrls: ['./purchase-limits-bar.component.scss']
})
export class PurchaseLimitsBarComponent implements OnInit, AfterViewInit {

  @ViewChild('accordionGroup', { static: true }) accordionGroup!: IonAccordionGroup;
  @ViewChild('targetRange', { read: ElementRef }) ionRange!: ElementRef;

  @Input() limits = { LimitMinCajas: 0, LimitMinMonto: 0, LimitMaxCajas: 0, LimitMaxMonto: 0, LimiteMinAdvertencia: 0 };
  @Input() set summaryPrice(value: ISummaryPrice) {
    this._summaryPrice = value || { total_cajas: 0, total_precio: 0, precio_descuento: 0, FactorCl: 0 };
    this.updateComputedValues();
  }
  @Input() remainingTime: string = '';

  public limitsOrderDisclaimers: Array<ILimitsOrderDisclaimer> = [];
  public purchaseStatus: 'perfect' | 'incomplete' | 'almost' | 'exceeded' | 'empty' = 'empty';
  public progressPercentage: number = 0;
  public statusColorBG: string = 'getm-information-light';
  public statusColorFill: string = 'getm-information-default';
  public cartIcon: string = 'cart-outline';

  private _summaryPrice: ISummaryPrice = { total_cajas: 0, total_precio: 0, precio_descuento: 0, FactorCl: 0 };

  constructor(private translate: TranslateService) { }

  ngOnInit() {
    // Inicializa los disclaimer de límites
    const limitesNames = Object.keys(this.limits);
    this.limitsOrderDisclaimers = this.translate.instant('Miscellaneous.limits-cart-disclaimers')
      .map((d: ILimitsOrderDisclaimer) => {
        const limit = d.disclaimer.match(/{{(.*?)}}/)?.[1] || '';
        const disclaimer = limitesNames.includes(limit);
        if (disclaimer && this.limits[limit] > 0) {
          const copy = limit.includes('Monto') ? this.limits[limit].toFixed(2) : this.limits[limit].toString();
          return {
            ...d,
            disclaimer: d.disclaimer.replace(`{{${limit}}}`, copy),
            active: true
          };
        }
        return d;
      });
  }

  ngAfterViewInit() {
    // Configura el estilo del rango para permitir transiciones
    const shadow = this.ionRange.nativeElement.shadowRoot;
    if (shadow) {
      const style = document.createElement('style');
      style.textContent = `
        .range-knob-handle {
          transition: left 0.3s ease-in-out, transform 0.3s ease-in-out !important;
        }
      `;
      shadow.appendChild(style);
    }
  }

  /**
   * INTERCEPTOR
   * Obtiene el summaryPrice actual
   * @returns SummaryPrice actual
   */
  public get summaryPrice(): ISummaryPrice {
    return this._summaryPrice;
  }

  /**
   * Actualiza los valores computados basados en las cajas y monto actuales
   */
  private updateComputedValues(): void {
    // Usar FactorCl para cajas y total_precio para monto
    const currentCajas = this._summaryPrice.FactorCl || 0;
    const currentMonto = this._summaryPrice.total_precio || 0;

    // Verificar si hay límites configurados
    const hasMinCajasLimit = this.limits.LimitMinCajas > 0;
    const hasMinMontoLimit = this.limits.LimitMinMonto > 0;
    const hasMaxCajasLimit = this.limits.LimitMaxCajas > 0;
    const hasMaxMontoLimit = this.limits.LimitMaxMonto > 0;
    const hasMinLimits = hasMinCajasLimit || hasMinMontoLimit;


    // Si no hay cajas, estado vacío
    if (this._summaryPrice.total_cajas < 1) {
      this.purchaseStatus = 'empty';
      this.progressPercentage = 0;
      this.statusColorBG = 'getm-information-light';
      this.statusColorFill = 'getm-information-default';
      this.cartIcon = 'getm-cart-empty';
      return;
    }

    // Verificar si excede o está en límite máximo
    const exceedsMaxCajas = hasMaxCajasLimit && currentCajas > this.limits.LimitMaxCajas;
    const exceedsMaxMonto = hasMaxMontoLimit && currentMonto > this.limits.LimitMaxMonto;

    if (exceedsMaxCajas || exceedsMaxMonto) {
      this.purchaseStatus = 'exceeded';
      this.progressPercentage = 100;
      this.statusColorBG = 'getm-error-light';
      this.statusColorFill = 'getm-error-default';
      this.cartIcon = 'getm-cart-exceeded';
      return;
    }

    // Verificar si cumple límites mínimos (perfect)
    const isPerfect = (hasMinCajasLimit && currentCajas >= this.limits.LimitMinCajas)
      || (hasMinMontoLimit && currentMonto >= this.limits.LimitMinMonto)
      || (!hasMinLimits);

    if (isPerfect) {
      this.purchaseStatus = 'perfect';
      this.progressPercentage = 100;
      this.statusColorBG = 'getm-success-light';
      this.statusColorFill = 'getm-success-default';
      this.cartIcon = 'getm-cart-perfect';
      return;
    }

    // Calcular progreso basado en límites disponibles
    let progressPercentage = 0;
    if (hasMinCajasLimit) {
      progressPercentage = Math.min((currentCajas / this.limits.LimitMinCajas) * 100, 100);
    } else if (hasMinMontoLimit) {
      progressPercentage = Math.min((currentMonto / this.limits.LimitMinMonto) * 100, 100);
    }

    this.progressPercentage = progressPercentage;

    // Determinar estado basado en progreso
    if (progressPercentage >= 66.66) { //se usan tercios para dividir el progreso
      this.purchaseStatus = 'almost';
      this.statusColorBG = 'getm-warning-light';
      this.statusColorFill = 'getm-warning-default';
      this.cartIcon = 'getm-cart-almost';
    } else {
      this.purchaseStatus = 'incomplete';
      this.statusColorBG = 'getm-category-especiales-del-mes-light';
      this.statusColorFill = 'getm-secondary-default';
      this.cartIcon = 'getm-cart-incomplete';
    }
  }

  /**
   * Despliega o retrae el accordion de límites
   */
  public toggleAccordion = () => {
    const nativeEl = this.accordionGroup;
    if (nativeEl.value === 'limits') {
      nativeEl.value = undefined;
    } else {
      nativeEl.value = 'limits';
    }
  };
}

interface ILimitsOrderDisclaimer {
  id: number;
  point: string;
  disclaimer: string;
  active: boolean;
}
